import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FollowUpDialog } from "@/components/FollowUpDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Phone, Check, CalendarClock, ExternalLink, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtDate } from "@/lib/constants";

export function FollowUpCard({ fu }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  const complete = async () => {
    try {
      await api.patch(`/followups/${fu.id}`, { action: "complete" });
      toast.success("Follow-up erledigt");
      qc.invalidateQueries();
    } catch {
      toast.error("Fehler");
    }
  };

  return (
    <div data-testid={`followup-card-${fu.id}`}
      className={cn("rounded-xl border bg-card p-4 transition-colors",
        fu.overdue ? "border-rose-500/40 bg-rose-500/[0.04]" : "border-border")}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => navigate(`/leads/${fu.lead_id}`)}
              className="font-semibold text-sm hover:text-primary transition-colors truncate">
              {fu.company}
            </button>
            {fu.lead_status && <StatusBadge status={fu.lead_status} />}
            {fu.overdue && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-rose-300">
                <AlertTriangle className="h-3 w-3" /> Überfällig
              </span>
            )}
          </div>
          <div className="mt-1 text-xs text-muted-foreground space-y-0.5">
            {fu.contact_person && <div>{fu.contact_person}</div>}
            {fu.reason && <div className="text-foreground/80">{fu.reason}</div>}
            <div>Fällig: {fmtDate(fu.due_date)}{fu.due_time ? ` · ${fu.due_time}` : ""}</div>
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {fu.phone && (
          <Button asChild size="sm" variant="secondary" className="h-8" data-testid={`followup-call-${fu.id}`}>
            <a href={`tel:${fu.phone}`}><Phone className="h-3.5 w-3.5 mr-1" /> Anrufen</a>
          </Button>
        )}
        <Button size="sm" variant="secondary" className="h-8" onClick={complete} data-testid={`followup-done-${fu.id}`}>
          <Check className="h-3.5 w-3.5 mr-1" /> Erledigt
        </Button>
        <Button size="sm" variant="ghost" className="h-8" onClick={() => setRescheduleOpen(true)} data-testid={`followup-reschedule-${fu.id}`}>
          <CalendarClock className="h-3.5 w-3.5 mr-1" /> Verschieben
        </Button>
        <Button size="sm" variant="ghost" className="h-8" onClick={() => navigate(`/leads/${fu.lead_id}`)} data-testid={`followup-open-${fu.id}`}>
          <ExternalLink className="h-3.5 w-3.5 mr-1" /> Öffnen
        </Button>
      </div>

      <FollowUpDialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}
        lead={{ company: fu.company }} followup={fu} />
    </div>
  );
}
